import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import {
  FileText, Key, Send, Download, Eye, XCircle,
  RefreshCw, BarChart3, Shield, Globe, Code, Copy, Check,
  ChevronDown, ChevronRight, Lock, Zap,
} from 'lucide-react';

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="absolute top-2 right-2 p-1.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs flex items-center gap-1">
      {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
    </button>
  );
};

const CodeBlock = ({ code, lang = 'bash' }) => (
  <div className="relative">
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-x-auto">
      <code>{code}</code>
    </pre>
    <CopyButton text={code} />
  </div>
);

const Endpoint = ({ method, path, description, permission, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const methodColors = {
    GET: 'bg-green-100 text-green-700 border-green-200',
    POST: 'bg-blue-100 text-blue-700 border-blue-200',
    PUT: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    DELETE: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left">
        {open ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
        <span className={`px-2 py-0.5 rounded text-xs font-bold border ${methodColors[method]}`}>{method}</span>
        <code className="text-sm font-mono text-gray-800 flex-1">{path}</code>
        {permission && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{permission}</span>}
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-3 bg-gray-50">
          <p className="text-sm text-gray-600">{description}</p>
          {children}
        </div>
      )}
    </div>
  );
};

const ParamTable = ({ params }) => (
  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
    <thead className="bg-gray-100">
      <tr>
        <th className="text-left px-3 py-2 font-medium text-gray-600">Field</th>
        <th className="text-left px-3 py-2 font-medium text-gray-600">Type</th>
        <th className="text-left px-3 py-2 font-medium text-gray-600">Required</th>
        <th className="text-left px-3 py-2 font-medium text-gray-600">Description</th>
      </tr>
    </thead>
    <tbody>
      {params.map((p, i) => (
        <tr key={i} className="border-t border-gray-100">
          <td className="px-3 py-2 font-mono text-xs text-gray-800">{p.name}</td>
          <td className="px-3 py-2 text-gray-600">{p.type}</td>
          <td className="px-3 py-2">{p.required ? <span className="text-green-600 font-medium">Yes</span> : <span className="text-gray-400">No</span>}</td>
          <td className="px-3 py-2 text-gray-600">{p.desc}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ApiDocsPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: Globe },
    { id: 'auth', label: 'Authentication', icon: Key },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'signing', label: 'Signing', icon: Send },
    { id: 'keys', label: 'Key Management', icon: RefreshCw },
    { id: 'errors', label: 'Error Codes', icon: Shield },
    { id: 'flow', label: 'Integration Flow', icon: Zap },
  ];

  // Use the configured API URL (from .env), or fall back to same origin (production behind reverse proxy)
  const baseUrl = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');

  return (
    <AppLayout showApiLink={false}>
      <div className="flex-1 overflow-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex gap-8">
        {/* Sidebar nav */}
        <nav className="w-56 flex-shrink-0 hidden lg:block">
          <div className="sticky top-24 space-y-1">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">SaaS API Reference</h3>
            {sections.map(s => {
              const Icon = s.icon;
              return (
                <button key={s.id} onClick={() => {
                  setActiveSection(s.id);
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === s.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                  }`}>
                  <Icon className="h-4 w-4" /> {s.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-10">

          {/* Overview */}
          <section id="overview" className="scroll-mt-24">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white mb-8">
              <h2 className="text-3xl font-bold mb-3">E-Sign SaaS API</h2>
              <p className="text-blue-100 text-lg mb-4">
                Integrate document signing into your application. Upload PDFs, place fields, get signing URLs, and download signed documents — all via REST API.
              </p>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2 font-mono text-sm">
                <Globe className="h-4 w-4" />
                <span>Base URL: {baseUrl}/api/v1/saas</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: Key, title: 'API Key Auth', desc: 'Authenticate with X-Access-Key + X-Access-Secret headers' },
                { icon: Lock, title: 'Permission-based', desc: 'Granular permissions per API key (documents, signing, webhooks)' },
                { icon: Globe, title: 'Cross-domain', desc: 'CORS-enabled for any authorized domain' },
              ].map((f, i) => (
                <div key={i} className="bg-white rounded-xl border p-5">
                  <f.icon className="h-8 w-8 text-blue-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-1">{f.title}</h4>
                  <p className="text-sm text-gray-500">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Authentication */}
          <section id="auth" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><Key className="h-6 w-6 text-blue-600" /> Authentication</h2>
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <p className="text-gray-600">Every request must include your API credentials as headers:</p>
              <div className="bg-gray-50 rounded-lg p-4">
                <table className="text-sm w-full">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 font-mono text-blue-600 font-medium pr-4">X-Access-Key</td>
                      <td className="py-2 text-gray-600">Your API key</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono text-blue-600 font-medium pr-4">X-Access-Secret</td>
                      <td className="py-2 text-gray-600">Your API secret</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500">Or pass as query params: <code className="bg-gray-100 px-1 rounded">?access_key=...&access_secret=...</code></p>
              <CodeBlock code={`curl -H "X-Access-Key: your-key" \\
     -H "X-Access-Secret: your-secret" \\
     ${baseUrl}/api/v1/saas/info`} />

              <h4 className="font-semibold text-gray-900 mt-4">Permissions</h4>
              <p className="text-sm text-gray-600">Each API key has granular permissions:</p>
              <CodeBlock lang="json" code={`{
  "documents": ["create", "read", "update", "delete"],
  "signing": ["read", "update"],
  "webhooks": ["create", "read"]
}`} />
            </div>
          </section>

          {/* Documents */}
          <section id="documents" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><FileText className="h-6 w-6 text-blue-600" /> Documents</h2>
            <div className="space-y-3">
              <Endpoint method="GET" path="/api/v1/saas/info" description="Returns your client info and permissions. Use to verify credentials." defaultOpen>
                <CodeBlock code={`# Response
{
  "service": "LegacyFA E-Sign SaaS API",
  "version": "1.0.0",
  "client": "Your App Name",
  "permissions": { "documents": ["create", "read"] }
}`} />
              </Endpoint>

              <Endpoint method="POST" path="/api/v1/saas/documents" description="Create a new document with PDF, recipients, and signature fields." permission="documents:create">
                <ParamTable params={[
                  { name: 'pdf', type: 'File', required: true, desc: 'PDF file (multipart)' },
                  { name: 'title', type: 'string', required: false, desc: 'Document title' },
                  { name: 'expires_in_days', type: 'number', required: false, desc: 'Expiry days (default: 14)' },
                  { name: 'email_message', type: 'string', required: false, desc: 'Message to signers' },
                  { name: 'recipients', type: 'JSON', required: true, desc: 'Array of {name, email, signing_order}' },
                  { name: 'signature_fields', type: 'JSON', required: true, desc: 'Array of field placements' },
                  { name: 'callback_url', type: 'string', required: false, desc: 'Webhook URL for status updates' },
                ]} />
                <CodeBlock code={`curl -X POST ${baseUrl}/api/v1/saas/documents \\
  -H "X-Access-Key: your-key" \\
  -H "X-Access-Secret: your-secret" \\
  -F "pdf=@contract.pdf" \\
  -F "title=Sales Contract" \\
  -F 'recipients=[{"name":"John","email":"john@co.com","signing_order":1}]' \\
  -F 'signature_fields=[{"field_type":"signature","page_number":1,"x_position":100,"y_position":500,"width":200,"height":60}]'`} />
              </Endpoint>

              <Endpoint method="GET" path="/api/v1/saas/documents" description="List all documents created by your API key. Supports pagination and status filter." permission="documents:read">
                <ParamTable params={[
                  { name: 'page', type: 'number', required: false, desc: 'Page number (default: 1)' },
                  { name: 'per_page', type: 'number', required: false, desc: 'Items per page (default: 20)' },
                  { name: 'status', type: 'string', required: false, desc: 'Filter: pending, completed, voided' },
                ]} />
              </Endpoint>

              <Endpoint method="GET" path="/api/v1/saas/documents/:id" description="Get full document detail including recipients, fields, and activity log." permission="documents:read" />

              <Endpoint method="GET" path="/api/v1/saas/documents/:id/download" description="Download the signed PDF (or original if not yet signed)." permission="documents:read">
                <p className="text-xs text-gray-500">Returns PDF file directly or redirects to S3 URL.</p>
              </Endpoint>

              <Endpoint method="POST" path="/api/v1/saas/documents/:id/void" description="Void (cancel) a document and prevent further signing." permission="documents:update" />
            </div>
          </section>

          {/* Signing */}
          <section id="signing" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><Send className="h-6 w-6 text-blue-600" /> Signing</h2>
            <div className="space-y-3">
              <Endpoint method="GET" path="/api/v1/saas/documents/:id/signing-url/:recipientId"
                description="Get a unique signing URL for a recipient. Embed in your UI via iframe or redirect." permission="signing:read">
                <CodeBlock code={`# Response
{
  "success": true,
  "recipient_id": "uuid",
  "recipient_name": "John Doe",
  "signing_url": "${baseUrl}/sign/abc123token",
  "status": "pending"
}`} />
              </Endpoint>

              <Endpoint method="POST" path="/api/v1/saas/documents/:id/send/:recipientId"
                description="Trigger the signing invitation email to a specific recipient." permission="signing:update" />
            </div>
          </section>

          {/* Key Management */}
          <section id="keys" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><RefreshCw className="h-6 w-6 text-blue-600" /> Key Management</h2>
            <div className="space-y-3">
              <Endpoint method="POST" path="/api/v1/saas/keys/rotate-secret" description="Rotate your API secret. The new secret is only shown once in the response — store it securely." />
              <Endpoint method="GET" path="/api/v1/saas/keys/usage" description="View your API key usage stats including document count and rate limits." />
            </div>
          </section>

          {/* Error Codes */}
          <section id="errors" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><Shield className="h-6 w-6 text-blue-600" /> Error Codes</h2>
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['401', 'Missing or invalid credentials'],
                    ['403', 'Valid credentials but insufficient permissions'],
                    ['404', 'Resource not found'],
                    ['429', 'Rate limit exceeded (100 req / 15 min)'],
                    ['500', 'Server error'],
                  ].map(([code, desc]) => (
                    <tr key={code} className="border-t border-gray-100">
                      <td className="px-4 py-3 font-mono font-bold text-red-600">{code}</td>
                      <td className="px-4 py-3 text-gray-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <CodeBlock lang="json" code={`{
  "error": "Error type",
  "message": "Human-readable description"
}`} />
            </div>
          </section>

          {/* Integration Flow */}
          <section id="flow" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><Zap className="h-6 w-6 text-blue-600" /> Integration Flow</h2>
            <div className="bg-white rounded-xl border p-6 space-y-6">
              <div className="space-y-4">
                {[
                  { step: 1, icon: Key, title: 'Get API Credentials', desc: 'Obtain your access_key and access_secret from the admin.' },
                  { step: 2, icon: Globe, title: 'Configure CORS', desc: 'Have your domain added to ALLOWED_ORIGINS on the API server.' },
                  { step: 3, icon: FileText, title: 'Create Document', desc: 'POST /saas/documents with PDF, recipients, and field positions.' },
                  { step: 4, icon: Eye, title: 'Get Signing URLs', desc: 'GET /saas/documents/:id/signing-url/:recipientId for each signer.' },
                  { step: 5, icon: Code, title: 'Embed in Your UI', desc: 'Use the signing URL in an iframe or redirect users to it.' },
                  { step: 6, icon: BarChart3, title: 'Track Progress', desc: 'Poll GET /saas/documents/:id or use webhook callbacks.' },
                  { step: 7, icon: Download, title: 'Download Signed PDF', desc: 'GET /saas/documents/:id/download once all have signed.' },
                ].map(s => (
                  <div key={s.step} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">{s.step}</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2"><s.icon className="h-4 w-4 text-blue-500" />{s.title}</h4>
                      <p className="text-sm text-gray-500">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 mb-2">Architecture</h4>
                <pre className="text-xs text-gray-600 font-mono leading-relaxed">{`┌──────────────────────────────────────────────┐
│         Your Application (any domain)        │
│                                              │
│  1. POST /saas/documents  (upload PDF)       │
│  2. GET  /saas/documents/:id/signing-url/:r  │
│  3. Embed signing URL in iframe / redirect   │
│  4. GET  /saas/documents/:id/download        │
└───────────────────┬──────────────────────────┘
                    │  X-Access-Key
                    │  X-Access-Secret
                    ▼
┌──────────────────────────────────────────────┐
│       LegacyFA E-Sign API Server             │
│       /api/v1/saas/*                         │
└──────────────────────────────────────────────┘`}</pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-1">Field Types</h4>
                <div className="flex flex-wrap gap-2">
                  {['signature', 'email', 'date_signed', 'textfield', 'date', 'checkbox'].map(t => (
                    <span key={t} className="bg-white border border-blue-200 text-blue-700 px-3 py-1 rounded-full text-xs font-mono">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
      </div>
    </AppLayout>
  );
};

export default ApiDocsPage;
